import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  Mail, 
  Menu, 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Network, 
  Code2, 
  ShieldCheck, 
  Laptop,
  Cloud,
  Database,
  Briefcase,
  Award,
  ArrowRight,
  BrainCircuit,
  ShoppingCart,
  Wifi,
  Server,
  Plus,
  Minus,
  Trash2,
  Lock,
  User,
  AlertCircle,
  Settings,
  UploadCloud,
  Package,
  Layers,
  Zap,
  Target,
  CheckCircle2,
  Building2,
  Users
} from 'lucide-react';

export default function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [logoError, setLogoError] = useState(false);
  
  // Navigation State
  const [currentPage, setCurrentPage] = useState('home'); // 'home' | 'shop' | 'admin' | 'about'
  
  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // Cart State
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // Admin State
  const [uploadStatus, setUploadStatus] = useState('');

  // About Page States
  const [activePod, setActivePod] = useState(0);
  const [compareMode, setCompareMode] = useState('bequest'); // 'traditional' | 'bequest'

  // --- Brand Colors ---
  const BRAND = {
    navyMain: '#040B16',
    navyLight: '#0A1128',
    cyan: '#00D4FF',
    cyanDark: '#0096C7',
    yellow: '#FFCC00',
    red: '#FF3B30'
  };

  // --- Helper function to apply 42% markup ---
  const applyMarkup = (basePrice) => {
    return Math.ceil(basePrice * 1.42);
  };

  // --- Initial Default Products ---
  const defaultProducts = [
    { id: 'def-1', name: "Huawei Wireless AP362 Wi-Fi 6", partner: "Huawei", category: "Networking", price: applyMarkup(1843), icon: <Wifi className="w-16 h-16" style={{color: BRAND.cyanDark}} /> },
    { id: 'def-2', name: "Huawei IdeaHub S2 65-inch", partner: "Huawei", category: "Interactive Displays", price: applyMarkup(66526), icon: <Laptop className="w-16 h-16" style={{color: BRAND.yellow}} /> },
    { id: 'def-3', name: "Yealink CP965 Android IP Conference Phone", partner: "Yealink", category: "Unified Communications", price: applyMarkup(8578), icon: <Phone className="w-16 h-16" style={{color: BRAND.cyan}} /> },
    { id: 'def-4', name: "Yealink MVC400 Video Conferencing Solution", partner: "Yealink", category: "Video Conferencing", price: applyMarkup(36813), icon: <Code2 className="w-16 h-16" style={{color: BRAND.red}} /> },
    { id: 'def-5', name: "MikroTik Cloud Smart Switch 24-Port", partner: "MikroTik", category: "Networking", price: applyMarkup(2283), icon: <Network className="w-16 h-16" style={{color: BRAND.cyanDark}} /> },
    { id: 'def-6', name: "MikroTik netFiber 9 Switch", partner: "MikroTik", category: "Networking", price: applyMarkup(3614), icon: <Network className="w-16 h-16" style={{color: BRAND.cyan}} /> },
    { id: 'def-7', name: "Jabra Evolve 20 Stereo Wired Headset MS", partner: "Jabra", category: "Unified Communications", price: applyMarkup(850), icon: <Briefcase className="w-16 h-16" style={{color: BRAND.yellow}} /> },
    { id: 'def-8', name: "Zyxel EX5301 Wi-Fi 6 Router with VoIP", partner: "Zyxel", category: "Networking", price: applyMarkup(1749), icon: <Wifi className="w-16 h-16" style={{color: BRAND.red}} /> },
    { id: 'def-9', name: "Teltonika RUT260 4G LTE Cat 6 Router", partner: "Teltonika", category: "Industrial Networking", price: applyMarkup(3035), icon: <ShieldCheck className="w-16 h-16" style={{color: BRAND.cyanDark}} /> },
    { id: 'def-10', name: "3CX Professional V20 16SC license (Annual)", partner: "3CX", category: "Software", price: applyMarkup(845), icon: <Cloud className="w-16 h-16" style={{color: BRAND.yellow}} /> },
    { id: 'def-11', name: "Shelly 1PM Gen3 Smart Switch", partner: "Shelly", category: "IoT / Smart Home", price: applyMarkup(365), icon: <ShieldCheck className="w-16 h-16" style={{color: BRAND.cyan}} /> }
  ];

  const [products, setProducts] = useState(defaultProducts);

  // --- Home Page Data ---
  const slides = [
    {
      showLogo: true,
      title: "Bridging the Gap in ICT",
      subtitle: "Bequest Group is a premier Information and Communication Technology consultancy and service provider.",
      color: "bg-[#040B16]"
    },
    {
      title: "Authorized Partner & Reseller",
      subtitle: "Proudly partnering with industry-leading brands to deliver world-class enterprise solutions.",
      color: "bg-[#0A1128]",
      logos: ["Adobe", "Tenable", "SonicWall", "Miro", "PDF-XChange", "HP", "Dell", "TP-Link", "Jabra", "Nology", "Esquire"]
    },
    {
      title: "Digital Infrastructure & Connectivity",
      subtitle: "Building the highways for your data with high-speed MPLS, VPNs, and secure Cloud solutions.",
      color: "bg-[#070D20]"
    },
    {
      title: "Enterprise Software & Applications",
      subtitle: "Designing bespoke software solutions and data intelligence tailored to your unique organizational processes.",
      color: "bg-[#050914]"
    }
  ];

  useEffect(() => {
    if (currentPage !== 'home') return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length, currentPage]);

  const nextSlide = () => setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  const prevSlide = () => setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));

  const technologies = ["Cisco", "Huawei", "Microsoft", "Linux", "ITIL v4", "PRINCE2", "COBIT 5", "ISO 27001", "POPIA"];

  const capabilities = [
    { icon: <Network className="w-10 h-10" style={{color: BRAND.cyan}} />, title: "Digital Infrastructure", desc: "Implementation of high-speed MPLS, VPNs, fiber connectivity, Cloud Hosting, and Disaster Recovery." },
    { icon: <Code2 className="w-10 h-10" style={{color: BRAND.yellow}} />, title: "Enterprise Software", desc: "Custom bespoke software development, ongoing application maintenance, and Business Intelligence (BI)." },
    { icon: <ShieldCheck className="w-10 h-10" style={{color: BRAND.red}} />, title: "Cybersecurity & Governance", desc: "Designing secure perimeters, conducting risk assessments (ISO/POPIA), and strategic IT governance." },
    { icon: <Laptop className="w-10 h-10" style={{color: BRAND.cyan}} />, title: "Modern Workplace", desc: "End-User Computing (EUC) supply, secure remote access (APN/VPNC), and centralized Service Desk support." }
  ];

  const pillars = [
    { icon: <Zap className="w-8 h-8 mb-4" style={{color: BRAND.cyan}}/>, title: "Innovation", desc: "Modernizing legacy environments leveraging cloud, connectivity, and bespoke software." },
    { icon: <Server className="w-8 h-8 mb-4" style={{color: BRAND.yellow}}/>, title: "Technology", desc: "Deploying robust, industrial-grade infrastructure designed for zero-downtime stability." },
    { icon: <Target className="w-8 h-8 mb-4" style={{color: BRAND.red}}/>, title: "Strategy", desc: "Mapping every technological deployment directly to long-term organizational goals and government mandates." }
  ];

  // --- About Page Data ---
  const podRoles = [
    { 
      title: "Network Architect", 
      icon: <Network className="w-8 h-8" />, 
      color: BRAND.cyan, 
      desc: "Designing high-availability WANS (BGP/OSPF) to ensure flawless connectivity across your enterprise." 
    },
    { 
      title: "Cloud Administrator", 
      icon: <Cloud className="w-8 h-8" />, 
      color: BRAND.yellow, 
      desc: "Hardening servers and ensuring strict RTO/RPO (Recovery Time/Point Objective) integrity for disaster recovery." 
    },
    { 
      title: "Software Developer", 
      icon: <Code2 className="w-8 h-8" />, 
      color: BRAND.red, 
      desc: "Translating complex business logic into secure, scalable, and user-friendly code." 
    },
    { 
      title: "Governance Consultant", 
      icon: <ShieldCheck className="w-8 h-8" />, 
      color: BRAND.cyanDark, 
      desc: "Identifying vulnerabilities and enforcing POPIA, COBIT 5, and ITIL v4 compliance across the entire digital estate." 
    }
  ];

  // --- Auth Functions ---
  const [authLoading, setAuthLoading] = useState(false);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');

    if (!email.includes('@')) {
      setAuthError('Please enter a valid email address.');
      return;
    }

    if (authMode === 'signup') {
      const alphanumericRegex = /^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+){8,}$/;
      if (!alphanumericRegex.test(password)) {
        setAuthError('Password must be at least 8 characters long and contain both letters and numbers (alphanumeric).');
        return;
      }
      if (password !== confirmPassword) {
        setAuthError('Passwords do not match.');
        return;
      }
    }

    setAuthLoading(true);
    try {
      const endpoint = authMode === 'signup' ? '/.netlify/functions/auth-signup' : '/.netlify/functions/auth-login';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setAuthError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('authUser', JSON.stringify(data.user));
      setIsAuthenticated(true);
      setAuthModalOpen(false);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setAuthError('Network error. Please check your connection and try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setIsAuthenticated(false);
    setCart([]);
    if (currentPage === 'admin') setCurrentPage('home');
  };

  // Restore auth state from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // --- Cart Functions ---
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, qty: 1 }];
    });
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.qty + delta);
        return { ...item, qty: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.qty, 0);


  // --- CSV Parsing Engine ---
  const parseCSVRow = (str) => {
    let arr = [];
    let quote = false;
    let cell = '';
    for (let c = 0; c < str.length; c++) {
      let cc = str[c], nc = str[c+1];
      if (cc === '"' && quote && nc === '"') { cell += '"'; c++; }
      else if (cc === '"') { quote = !quote; }
      else if (cc === ',' && !quote) { arr.push(cell); cell = ''; }
      else { cell += cc; }
    }
    if (cell !== undefined) arr.push(cell.trim());
    return arr;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadStatus('Reading file...');

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim() !== '');
      let headerIndex = -1;
      let headers = [];

      for (let i = 0; i < lines.length; i++) {
        const cols = parseCSVRow(lines[i]).map(c => c.toLowerCase());
        if (cols.includes('description') && (cols.includes('price') || cols.includes('standard price'))) {
          headerIndex = i;
          headers = cols;
          break;
        }
      }

      if (headerIndex === -1) {
        setUploadStatus('❌ Error: Could not find "DESCRIPTION" and "PRICE" columns in the CSV.');
        return;
      }

      const descIdx = headers.indexOf('description');
      const priceIdx = headers.includes('price') ? headers.indexOf('price') : headers.indexOf('standard price');
      const skuIdx = headers.includes('sku') ? headers.indexOf('sku') : headers.indexOf('code');

      const importedProducts = [];
      let currentCategory = "Imported Equipment";

      for (let i = headerIndex + 1; i < lines.length; i++) {
        const cols = parseCSVRow(lines[i]);
        if (cols.length <= Math.max(descIdx, priceIdx)) continue;

        const desc = cols[descIdx];
        const priceStr = cols[priceIdx];
        const sku = skuIdx !== -1 ? cols[skuIdx] : '';

        if (desc && (!priceStr || isNaN(parseFloat(priceStr.replace(/[^0-9.-]+/g,""))))) {
          if (sku && !desc.includes(',')) currentCategory = sku;
          else if (desc.length < 30) currentCategory = desc; 
          continue;
        }

        const rawPrice = parseFloat(priceStr.replace(/[^0-9.-]+/g,""));
        if (!desc || isNaN(rawPrice) || rawPrice <= 0) continue;

        importedProducts.push({
          id: `csv-${Date.now()}-${i}`,
          name: desc,
          partner: "Imported via CSV",
          category: currentCategory,
          price: applyMarkup(rawPrice),
          icon: <Package className="w-16 h-16" style={{color: BRAND.cyanDark}} />
        });
      }

      if (importedProducts.length > 0) {
        setProducts(prev => [...importedProducts, ...prev]); 
        setUploadStatus(`✅ Successfully imported ${importedProducts.length} products. 42% markup applied!`);
      } else {
        setUploadStatus('⚠️ No valid products with prices found in the CSV.');
      }
      event.target.value = null;
    };
    reader.readAsText(file);
  };


  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 selection:bg-[#00D4FF] selection:text-[#040B16] flex flex-col">
      
      {/* Top Contact Bar */}
      <div className="bg-[#02050A] text-slate-300 text-xs py-2 hidden md:block border-b border-[#0A1128]">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex space-x-6">
            <span className="flex items-center"><Phone className="w-3 h-3 mr-2" style={{color: BRAND.cyan}} /> +27 62 814 9629</span>
            <span className="flex items-center"><Mail className="w-3 h-3 mr-2" style={{color: BRAND.cyan}} /> admin@sabequest.co.za</span>
          </div>
          <div className="flex space-x-4 items-center">
            <span className="flex items-center font-bold" style={{color: BRAND.yellow}}><Award className="w-4 h-4 mr-1" /> B-BBEE Level 1 Contributor</span>
            <span className="text-slate-600">|</span>
            <span className="text-slate-300">100% Black Owned</span>
            <span className="text-slate-600">|</span>
            <span className="text-slate-300 font-bold" style={{color: BRAND.cyan}}>CSD: MAAA1656690</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-[#040B16] text-white shadow-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-24">
            
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center cursor-pointer group" onClick={() => setCurrentPage('home')}>
              {!logoError ? (
                <img src="./bequest group Logo.png" alt="Bequest Group Logo" className="h-16 w-auto mr-3 object-contain" onError={() => setLogoError(true)} />
              ) : (
                <>
                  <div className="mr-3 p-2 bg-gradient-to-br from-[#00D4FF] via-[#FFCC00] to-[#FF3B30] rounded-xl group-hover:scale-105 transition-transform shadow-lg shadow-[#00D4FF]/20">
                     <BrainCircuit className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex flex-col justify-center">
                    <span className="text-2xl font-black tracking-widest text-white leading-none mb-1">BEQUEST <span style={{color: BRAND.cyan}}>GROUP</span></span>
                    <span className="text-[0.65rem] tracking-[0.2em] font-light uppercase text-center flex justify-between" style={{color: BRAND.cyan}}>
                      <span>Innovation</span><span style={{color: BRAND.red}}>•</span><span>Technology</span><span style={{color: BRAND.yellow}}>•</span><span>Strategy</span>
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex space-x-8 items-center font-semibold text-sm text-slate-300">
              <button onClick={() => setCurrentPage('home')} className={`${currentPage === 'home' ? 'text-[#00D4FF]' : 'text-white hover:text-[#00D4FF]'} transition`}>Home</button>
              <button onClick={() => setCurrentPage('about')} className={`${currentPage === 'about' ? 'text-[#00D4FF]' : 'hover:text-[#00D4FF]'} transition flex items-center`}>About Us</button>
              <button onClick={() => setCurrentPage('shop')} className={`${currentPage === 'shop' ? 'text-[#00D4FF]' : 'hover:text-[#00D4FF]'} transition flex items-center`}>Products & Solutions</button>
              
              {/* Admin Link */}
              {isAuthenticated && (
                <button onClick={() => setCurrentPage('admin')} className={`${currentPage === 'admin' ? 'text-[#00D4FF]' : 'hover:text-[#00D4FF]'} transition flex items-center`}>
                  <Settings className="w-4 h-4 mr-1"/> Admin
                </button>
              )}

              <a href="mailto:admin@sabequest.co.za" className="px-5 py-2.5 rounded-md transition font-bold" style={{backgroundColor: BRAND.cyan, color: BRAND.navyMain}}>Contact Us</a>
              
              {/* Auth / Account */}
              {isAuthenticated ? (
                <button onClick={handleLogout} className="text-slate-300 hover:text-[#FF3B30] transition flex items-center">
                  <User className="w-5 h-5 mr-1" /> Logout
                </button>
              ) : (
                <button onClick={() => { setAuthMode('signup'); setAuthModalOpen(true); }} className="text-slate-300 hover:text-[#00D4FF] transition flex items-center">
                  <User className="w-5 h-5 mr-1" /> Sign Up
                </button>
              )}

              {/* Cart Icon */}
              {isAuthenticated && (
                <button onClick={() => setIsCartOpen(true)} className="relative p-2 text-slate-300 hover:text-[#00D4FF] transition">
                  <ShoppingCart className="w-6 h-6" />
                  {cartItemCount > 0 && (
                    <span className="absolute top-0 right-0 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" style={{backgroundColor: BRAND.red}}>
                      {cartItemCount}
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-4">
               {isAuthenticated && (
                 <button onClick={() => setIsCartOpen(true)} className="relative p-2 text-slate-300 hover:text-[#00D4FF] transition">
                  <ShoppingCart className="w-6 h-6" />
                  {cartItemCount > 0 && (
                    <span className="absolute top-0 right-0 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" style={{backgroundColor: BRAND.red}}>
                      {cartItemCount}
                    </span>
                  )}
                </button>
               )}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-slate-300 hover:text-white focus:outline-none">
                {isMobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-[#040B16] border-t border-[#0A1128] absolute w-full left-0 shadow-2xl z-50">
            <div className="px-4 pt-2 pb-6 space-y-2 text-base font-medium text-slate-300">
              <button onClick={() => {setCurrentPage('home'); setIsMobileMenuOpen(false);}} className={`w-full text-left px-3 py-3 rounded-md ${currentPage === 'home' ? 'text-white bg-[#0A1128]' : 'hover:bg-[#0A1128]'}`}>Home</button>
              <button onClick={() => {setCurrentPage('about'); setIsMobileMenuOpen(false);}} className={`w-full text-left px-3 py-3 rounded-md ${currentPage === 'about' ? 'text-white bg-[#0A1128]' : 'hover:bg-[#0A1128]'}`}>About Us</button>
              <button onClick={() => {setCurrentPage('shop'); setIsMobileMenuOpen(false);}} className={`w-full text-left px-3 py-3 rounded-md ${currentPage === 'shop' ? 'text-white bg-[#0A1128]' : 'hover:bg-[#0A1128]'}`}>Products & Solutions</button>
              {isAuthenticated && (
                <button onClick={() => {setCurrentPage('admin'); setIsMobileMenuOpen(false);}} className={`w-full text-left px-3 py-3 rounded-md ${currentPage === 'admin' ? 'text-white bg-[#0A1128]' : 'hover:bg-[#0A1128]'}`}>Admin Dashboard</button>
              )}
              {!isAuthenticated && (
                 <button onClick={() => { setAuthMode('signup'); setAuthModalOpen(true); setIsMobileMenuOpen(false); }} className="w-full text-left px-3 py-3 hover:bg-[#0A1128] rounded-md" style={{color: BRAND.cyan}}>Sign Up / Login</button>
              )}
              {isAuthenticated && (
                 <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="w-full text-left px-3 py-3 hover:bg-[#0A1128] rounded-md" style={{color: BRAND.red}}>Logout</button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content Area */}
      <div className="flex-grow">
        
        {currentPage === 'about' && (
          // --- ABOUT US PAGE CONTENT ---
          <div className="bg-slate-50 min-h-screen">
            
            {/* About Hero */}
            <div className="relative py-24 md:py-32 bg-slate-900 overflow-hidden" style={{backgroundColor: BRAND.navyMain}}>
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
              <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full blur-[120px] opacity-40 pointer-events-none" style={{backgroundColor: BRAND.cyan}}></div>
              
              <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                <div className="inline-flex items-center justify-center p-3 rounded-full mb-6" style={{backgroundColor: `${BRAND.cyan}20`}}>
                  <BrainCircuit className="w-6 h-6 mr-2" style={{color: BRAND.cyan}} />
                  <span className="font-bold tracking-widest uppercase text-sm" style={{color: BRAND.cyan}}>Bridging the Gap</span>
                </div>
                <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight max-w-4xl mx-auto">
                  Architects of the Integrated <span style={{color: BRAND.cyan}}>Digital Estate.</span>
                </h1>
                <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto font-light leading-relaxed">
                  From the physical cable in the wall to the strategic software policy in the boardroom. 
                  Bequest Group is a premier Information and Communication Technology consultancy eliminating vendor silos.
                </p>
              </div>
            </div>

            {/* The Integration Advantage (Interactive Toggle) */}
            <div className="py-24 bg-white relative">
              <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-16">
                  <h2 className="text-sm font-bold tracking-widest uppercase mb-3" style={{color: BRAND.cyanDark}}>The Bequest Approach</h2>
                  <h3 className="text-3xl md:text-4xl font-black text-slate-900">The Integration Advantage</h3>
                </div>

                <div className="max-w-4xl mx-auto">
                  {/* Interactive Toggle */}
                  <div className="flex bg-slate-100 p-1 rounded-2xl mb-10 shadow-inner">
                    <button 
                      onClick={() => setCompareMode('traditional')}
                      className={`flex-1 py-4 px-6 rounded-xl font-bold text-sm md:text-lg transition-all duration-300 ${compareMode === 'traditional' ? 'bg-white shadow-md text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      Traditional IT
                    </button>
                    <button 
                      onClick={() => setCompareMode('bequest')}
                      className={`flex-1 py-4 px-6 rounded-xl font-bold text-sm md:text-lg transition-all duration-300 ${compareMode === 'bequest' ? 'shadow-md text-white' : 'text-slate-500 hover:text-slate-700'}`}
                      style={compareMode === 'bequest' ? {backgroundColor: BRAND.navyMain} : {}}
                    >
                      Bequest SA
                    </button>
                  </div>

                  {/* Toggle Content */}
                  <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100 min-h-[300px] flex items-center transition-all duration-500">
                    {compareMode === 'traditional' ? (
                      <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid md:grid-cols-2 gap-8">
                          <div className="bg-red-50 p-6 rounded-2xl border border-red-100">
                            <Layers className="w-10 h-10 text-red-400 mb-4" />
                            <h4 className="text-xl font-bold text-red-900 mb-2">Fragmented Delivery</h4>
                            <p className="text-red-700">Multi-vendor silos with competing priorities leading to accountability gaps.</p>
                          </div>
                          <div className="bg-red-50 p-6 rounded-2xl border border-red-100">
                            <Laptop className="w-10 h-10 text-red-400 mb-4" />
                            <h4 className="text-xl font-bold text-red-900 mb-2">Isolated Infrastructure</h4>
                            <p className="text-red-700">Hardware and software built in isolation, leading to operational friction and downtime.</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid md:grid-cols-2 gap-8">
                          <div className="p-6 rounded-2xl border" style={{backgroundColor: `${BRAND.cyan}10`, borderColor: `${BRAND.cyan}30`}}>
                            <CheckCircle2 className="w-10 h-10 mb-4" style={{color: BRAND.cyanDark}} />
                            <h4 className="text-xl font-bold text-slate-900 mb-2">Accountable Delivery</h4>
                            <p className="text-slate-700">A single point of contact providing end-to-end accountability for complex requirements.</p>
                          </div>
                          <div className="p-6 rounded-2xl border" style={{backgroundColor: `${BRAND.yellow}10`, borderColor: `${BRAND.yellow}50`}}>
                            <Code2 className="w-10 h-10 mb-4" style={{color: '#D4A000'}} />
                            <h4 className="text-xl font-bold text-slate-900 mb-2">Co-Optimized Stacks</h4>
                            <p className="text-slate-700">Hardware and software designed to run flawlessly together from day one.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Integrated Expertise: The Cross-Functional Pod (Interactive) */}
            <div className="py-24 bg-slate-50 border-t border-slate-200">
              <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-16">
                  <h2 className="text-sm font-bold tracking-widest uppercase mb-3" style={{color: BRAND.cyanDark}}>Our Team Structure</h2>
                  <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">The Cross-Functional Pod</h3>
                  <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                    We do not operate in silos. Network engineers work alongside app developers to ensure software is optimized for the infrastructure it runs on.
                  </p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 items-center bg-white p-8 md:p-12 rounded-3xl shadow-lg border border-slate-100">
                  
                  {/* Left: Interactive Diagram */}
                  <div className="lg:w-1/2 w-full relative h-[400px] flex items-center justify-center">
                    {/* Central Node */}
                    <div className="absolute z-20 w-32 h-32 rounded-full shadow-2xl flex flex-col items-center justify-center transition-all duration-300" style={{backgroundColor: BRAND.navyMain}}>
                      <Building2 className="w-8 h-8 mb-2" style={{color: BRAND.cyan}} />
                      <span className="text-white font-bold text-sm text-center leading-tight">Client<br/>Project</span>
                    </div>

                    {/* Surrounding Nodes */}
                    {podRoles.map((pod, index) => {
                      const angle = (index * (360 / podRoles.length)) * (Math.PI / 180);
                      const radius = 140; // Distance from center
                      const x = Math.cos(angle) * radius;
                      const y = Math.sin(angle) * radius;
                      
                      const isActive = activePod === index;

                      return (
                        <div 
                          key={index}
                          onClick={() => setActivePod(index)}
                          className={`absolute z-30 cursor-pointer transition-all duration-300 transform ${isActive ? 'scale-110 shadow-xl ring-4 ring-offset-2' : 'scale-100 shadow-md hover:scale-105'} flex items-center justify-center w-20 h-20 rounded-full bg-white border-2`}
                          style={{
                            transform: `translate(${x}px, ${y}px) scale(${isActive ? 1.15 : 1})`,
                            borderColor: isActive ? pod.color : '#E2E8F0',
                            ringColor: `${pod.color}50`
                          }}
                        >
                          {React.cloneElement(pod.icon, { style: { color: pod.color }})}
                        </div>
                      );
                    })}

                    {/* Connecting Lines (SVG) */}
                    <svg className="absolute inset-0 w-full h-full z-10 pointer-events-none">
                       {podRoles.map((pod, index) => {
                          const angle = (index * (360 / podRoles.length)) * (Math.PI / 180);
                          const radius = 140; 
                          const x = Math.cos(angle) * radius + 200; // 200 is center offset roughly
                          const y = Math.sin(angle) * radius + 200;
                          return (
                            <line 
                              key={index} 
                              x1="50%" y1="50%" 
                              x2={`calc(50% + ${Math.cos(angle) * radius}px)`} 
                              y2={`calc(50% + ${Math.sin(angle) * radius}px)`} 
                              stroke={activePod === index ? pod.color : "#E2E8F0"} 
                              strokeWidth={activePod === index ? "4" : "2"} 
                              className="transition-all duration-500"
                            />
                          )
                       })}
                    </svg>
                  </div>

                  {/* Right: Dynamic Content Panel */}
                  <div className="lg:w-1/2 w-full">
                    <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200 h-full min-h-[300px] flex flex-col justify-center animate-in fade-in duration-300">
                      <div className="inline-flex items-center justify-center p-4 rounded-2xl mb-6 shadow-sm bg-white" style={{color: podRoles[activePod].color}}>
                        {React.cloneElement(podRoles[activePod].icon, { className: "w-10 h-10" })}
                      </div>
                      <h4 className="text-3xl font-black text-slate-900 mb-4">{podRoles[activePod].title}</h4>
                      <p className="text-lg text-slate-600 leading-relaxed mb-8">
                        {podRoles[activePod].desc}
                      </p>
                      <div className="flex space-x-2">
                        {podRoles.map((_, idx) => (
                          <div 
                            key={idx} 
                            className={`h-2 rounded-full transition-all duration-300 ${activePod === idx ? 'w-12' : 'w-3 bg-slate-300 cursor-pointer'}`}
                            style={activePod === idx ? {backgroundColor: podRoles[idx].color} : {}}
                            onClick={() => setActivePod(idx)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Industrial-Grade Certifications */}
            <div className="py-24 text-white relative overflow-hidden" style={{backgroundColor: BRAND.navyMain}}>
              <div className="max-w-7xl mx-auto px-4 relative z-10">
                <div className="text-center mb-16">
                  <h2 className="text-sm font-bold tracking-widest uppercase mb-3" style={{color: BRAND.yellow}}>Our Qualifications</h2>
                  <h3 className="text-3xl md:text-4xl font-black text-white">Industrial-Grade Certifications</h3>
                  <p className="text-slate-400 mt-4 max-w-2xl mx-auto">We maintain active standing with leading global technology vendors and institutes.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Networking */}
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition">
                    <Network className="w-12 h-12 mb-6" style={{color: BRAND.cyan}} />
                    <h4 className="text-2xl font-bold mb-4">Networking</h4>
                    <ul className="space-y-3 text-slate-300">
                      <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-400"/> Cisco (CCNA/CCNP)</li>
                      <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-400"/> Huawei (HCIP)</li>
                    </ul>
                  </div>

                  {/* Systems */}
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition">
                    <Server className="w-12 h-12 mb-6" style={{color: BRAND.yellow}} />
                    <h4 className="text-2xl font-bold mb-4">Systems</h4>
                    <ul className="space-y-3 text-slate-300">
                      <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-400"/> Microsoft Certified Solutions Experts (MCSE)</li>
                      <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-400"/> Linux Professional Institute</li>
                    </ul>
                  </div>

                  {/* Governance */}
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition">
                    <ShieldCheck className="w-12 h-12 mb-6" style={{color: BRAND.red}} />
                    <h4 className="text-2xl font-bold mb-4">Governance & Security</h4>
                    <ul className="space-y-3 text-slate-300">
                      <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-400"/> COBIT 5</li>
                      <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-400"/> ITIL v4</li>
                      <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-400"/> CISM (Certified Info Security Manager)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        {currentPage === 'admin' && isAuthenticated && (
           // --- ADMIN DASHBOARD ---
           <div className="bg-slate-50 min-h-screen py-12">
              <div className="max-w-7xl mx-auto px-4">
                <div className="mb-10 flex items-center justify-between">
                  <div>
                    <h1 className="text-4xl font-black text-slate-900 mb-2">Admin Dashboard</h1>
                    <p className="text-slate-600">Manage your product catalog and import distributor pricing.</p>
                  </div>
                  <Settings className="w-10 h-10 text-slate-300" />
                </div>

                {/* Upload Section */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 mb-8">
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                    <UploadCloud className="w-6 h-6 mr-2" style={{color: BRAND.cyanDark}}/> 
                    Import Nology Price List (CSV)
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">Upload your reseller CSV files here. The system will automatically extract the products, calculate a <strong style={{color: BRAND.red}}>42% markup</strong>, and add them to your live store.</p>
                  
                  <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-10 bg-slate-50" style={{borderColor: BRAND.cyan}}>
                    <input 
                      type="file" 
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden" 
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer flex flex-col items-center">
                      <UploadCloud className="w-12 h-12 mb-3" style={{color: BRAND.cyan}} />
                      <span className="bg-slate-900 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-800 transition">Select CSV File</span>
                    </label>
                  </div>

                  {uploadStatus && (
                    <div className={`mt-4 p-4 rounded-lg font-semibold text-sm ${uploadStatus.includes('❌') || uploadStatus.includes('⚠️') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                      {uploadStatus}
                    </div>
                  )}
                </div>

                {/* Product Management Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-900 text-white">
                    <h3 className="text-lg font-bold flex items-center"><Database className="w-5 h-5 mr-2" style={{color: BRAND.cyan}}/> Current Catalog ({products.length} Items)</h3>
                    <button 
                      onClick={() => { setProducts(defaultProducts); setUploadStatus('Reverted to default catalog.'); }} 
                      className="text-xs font-bold px-3 py-1 bg-red-600 hover:bg-red-500 rounded text-white transition"
                    >
                      Reset Catalog
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
                          <th className="p-4 font-bold">Product Name</th>
                          <th className="p-4 font-bold">Category</th>
                          <th className="p-4 font-bold">Selling Price (Inc. 42%)</th>
                          <th className="p-4 font-bold text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {products.map((p) => (
                          <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                            <td className="p-4 font-semibold text-slate-800">{p.name}</td>
                            <td className="p-4 text-slate-600">{p.category}</td>
                            <td className="p-4 font-bold" style={{color: BRAND.cyanDark}}>R {p.price.toLocaleString()}</td>
                            <td className="p-4 text-center">
                              <button 
                                onClick={() => setProducts(products.filter(item => item.id !== p.id))}
                                className="text-slate-400 hover:text-red-500 transition"
                              >
                                <Trash2 className="w-4 h-4 mx-auto" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
           </div>
        )}

        {currentPage === 'home' && (
          // --- HOME PAGE CONTENT ---
          <>
            <div className="relative h-[500px] md:h-[600px] w-full overflow-hidden" style={{backgroundColor: BRAND.navyMain}}>
              {slides.map((slide, index) => (
                <div 
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out flex flex-col justify-center items-center text-center px-4 ${slide.color} ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                >
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
                  
                  <div className="relative z-20 max-w-5xl mx-auto w-full">
                    {slide.showLogo && (
                      <div className="mb-8 flex justify-center">
                        <img src="./bequest group Logo.png" alt="Bequest Group Logo" className="h-24 md:h-32 w-auto object-contain drop-shadow-2xl" />
                      </div>
                    )}
                    <h1 className="text-4xl md:text-6xl font-black text-white mb-6 drop-shadow-2xl leading-tight">
                      {slide.title}
                    </h1>
                    <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto font-light">
                      {slide.subtitle}
                    </p>
                    
                    {slide.logos && (
                      <div className="mt-8 flex flex-wrap justify-center gap-4 md:gap-8 max-w-4xl mx-auto bg-black/30 p-8 rounded-2xl backdrop-blur-sm border border-white/10">
                        {slide.logos.map((logo, i) => (
                           <div key={i} className="text-white/80 font-bold text-xl md:text-2xl tracking-wider uppercase hover:text-white transition cursor-default flex items-center">
                             {logo}
                           </div>
                        ))}
                      </div>
                    )}

                    {!slide.logos && (
                      <button 
                        onClick={() => setCurrentPage('shop')} 
                        className="font-bold py-4 px-10 rounded-full transition transform hover:scale-105 shadow-xl shadow-[#00D4FF]/20"
                        style={{backgroundColor: BRAND.cyan, color: BRAND.navyMain}}
                      >
                        Explore Our Shop
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button onClick={prevSlide} className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/40 hover:bg-black/70 text-white p-3 rounded-full backdrop-blur-sm transition border border-white/10">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={nextSlide} className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/40 hover:bg-black/70 text-white p-3 rounded-full backdrop-blur-sm transition border border-white/10">
                <ChevronRight className="w-6 h-6" />
              </button>

              <div className="absolute bottom-8 left-0 right-0 z-30 flex justify-center space-x-3">
                {slides.map((_, index) => (
                  <button 
                    key={index} 
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-10' : 'bg-white/40 hover:bg-white/80 w-2'}`}
                    style={index === currentSlide ? {backgroundColor: BRAND.cyan} : {}}
                  />
                ))}
              </div>
            </div>

            <div className="bg-white border-b border-slate-200 py-6 overflow-hidden shadow-inner">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex flex-wrap justify-center md:justify-between items-center gap-6 opacity-70 font-bold text-sm md:text-lg text-slate-600">
                  {technologies.map((tech, idx) => (
                    <span key={idx} className="uppercase tracking-widest cursor-default border-l-2 pl-2" style={{borderColor: BRAND.cyan}}>
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-20">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-sm font-bold tracking-widest uppercase mb-3" style={{color: BRAND.cyanDark}}>What We Do</h2>
                <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">Our Core Capabilities</h3>
                <p className="text-slate-600 text-lg">Bequest Group offers an end-to-end portfolio of ICT services, acting as a single point of contact for all your digital requirements.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {capabilities.map((cap, idx) => (
                  <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-slate-100 group flex items-start">
                    <div className="bg-slate-50 p-4 rounded-xl mr-6 transition-colors duration-300" style={{border: `1px solid ${BRAND.cyan}40`}}>
                      {cap.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-800 mb-3 transition-colors" style={{color: BRAND.navyMain}}>{cap.title}</h3>
                      <p className="text-slate-600 leading-relaxed mb-4">
                        {cap.desc}
                      </p>
                      <button onClick={() => setCurrentPage('shop')} className="font-bold flex items-center text-sm" style={{color: BRAND.cyanDark}}>
                        View Solutions <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-2 transition-transform" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-white py-20 relative overflow-hidden" style={{backgroundColor: BRAND.navyMain}}>
              <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full blur-[100px] opacity-30" style={{backgroundColor: BRAND.cyan}}></div>
              <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 rounded-full blur-[100px] opacity-30" style={{backgroundColor: BRAND.red}}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-[100px] opacity-20" style={{backgroundColor: BRAND.yellow}}></div>

              <div className="max-w-7xl mx-auto px-4 relative z-10">
                <div className="flex flex-col md:flex-row justify-between items-end mb-16">
                  <div className="max-w-2xl">
                    <h2 className="text-sm font-bold tracking-widest uppercase mb-3" style={{color: BRAND.cyan}}>Why Choose Us</h2>
                    <h3 className="text-3xl md:text-5xl font-black mb-6 text-white">The Future-Ready Agility Framework</h3>
                    <p className="text-slate-300 text-lg">We combine the expertise of a large firm with the agility and personalized attention of a specialized consultancy.</p>
                  </div>
                  <div className="mt-6 md:mt-0">
                    <button onClick={() => setCurrentPage('about')} className="text-white border border-white/30 hover:bg-white/10 px-6 py-3 rounded-full font-bold transition flex items-center">
                      Read our Full Story <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {pillars.map((pillar, idx) => (
                    <div key={idx} className="backdrop-blur-sm p-8 rounded-2xl border transition-all duration-300 hover:transform hover:-translate-y-2" style={{backgroundColor: `${BRAND.navyLight}80`, borderColor: `${BRAND.cyan}40`}}>
                      {pillar.icon}
                      <h4 className="text-2xl font-bold mb-4 text-white">{pillar.title}</h4>
                      <p className="text-slate-400 leading-relaxed">{pillar.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {currentPage === 'shop' && (
          // --- SHOP PAGE CONTENT ---
          <div className="bg-slate-50 min-h-screen py-12">
            <div className="max-w-7xl mx-auto px-4">
              
              <div className="mb-12 text-center md:text-left flex flex-col md:flex-row justify-between items-center bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                <div className="mb-6 md:mb-0">
                  <h1 className="text-4xl font-black text-slate-900 mb-4">Partner Products & Solutions</h1>
                  <p className="text-lg text-slate-600 max-w-2xl">
                    Browse our catalog of authorized enterprise-grade hardware, software licenses, and security packages.
                  </p>
                </div>
                {!isAuthenticated && (
                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex items-start max-w-sm">
                     <Lock className="w-6 h-6 mr-3 flex-shrink-0 mt-1" style={{color: BRAND.yellow}} />
                     <div>
                       <h4 className="font-bold text-amber-900 mb-1">Pricing is Hidden</h4>
                       <p className="text-sm text-amber-700 mb-2">Create an account to view our exclusive B2B pricing and access purchasing options.</p>
                       <button onClick={() => { setAuthMode('signup'); setAuthModalOpen(true); }} className="text-sm font-bold hover:underline" style={{color: BRAND.cyanDark}}>Sign Up Now</button>
                     </div>
                  </div>
                )}
              </div>

              {/* Dynamic Product Grid mapped from State */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {products.map(product => (
                  <div key={product.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-shadow border border-slate-100 overflow-hidden flex flex-col group hover:border-[#00D4FF]/40">
                    <div className="h-48 bg-slate-50 flex items-center justify-center p-6 border-b border-slate-100 relative group-hover:bg-slate-100 transition-colors">
                       {product.partner && (
                         <span className="absolute top-4 right-4 text-white text-[10px] uppercase font-bold px-2 py-1 rounded" style={{backgroundColor: BRAND.navyLight}}>
                           {product.partner}
                         </span>
                       )}
                       <div className="transform group-hover:scale-110 transition-transform duration-300">
                         {product.icon}
                       </div>
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <span className="text-xs font-bold uppercase tracking-wider mb-2 line-clamp-1" style={{color: BRAND.cyanDark}}>{product.category}</span>
                      <h3 className="text-lg font-bold text-slate-800 mb-2 leading-snug flex-grow">{product.name}</h3>
                      
                      {isAuthenticated ? (
                        <>
                          <div className="mt-4 mb-6">
                            <span className="text-2xl font-black text-slate-900">R {product.price.toLocaleString()}</span>
                            {product.name.includes("Annual") || product.name.includes("License") ? <span className="text-sm text-slate-500 font-normal"> /term</span> : ""}
                          </div>
                          <button 
                            onClick={() => addToCart(product)}
                            className="w-full font-bold py-3 px-4 rounded-xl transition duration-200 flex items-center justify-center"
                            style={{backgroundColor: BRAND.navyMain, color: BRAND.cyan}}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = BRAND.cyan; e.currentTarget.style.color = BRAND.navyMain; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = BRAND.navyMain; e.currentTarget.style.color = BRAND.cyan; }}
                          >
                            <ShoppingCart className="w-5 h-5 mr-2" /> Add to Cart
                          </button>
                        </>
                      ) : (
                        <div className="mt-auto pt-6">
                          <button 
                            onClick={() => { setAuthMode('signup'); setAuthModalOpen(true); }}
                            className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 font-bold py-3 px-4 rounded-xl transition duration-200 flex items-center justify-center"
                          >
                            <Lock className="w-4 h-4 mr-2 text-slate-500" /> Sign Up to View Price
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="text-slate-400 pt-20 pb-10 border-t-4 mt-auto" style={{backgroundColor: BRAND.navyMain, borderColor: BRAND.cyan}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1 md:col-span-2 lg:col-span-1">
              <div className="flex items-center mb-6">
                {!logoError ? (
                  <img src="./bequest group Logo.png" alt="Logo" className="h-10 w-auto mr-2" onError={() => setLogoError(true)} />
                ) : (
                  <BrainCircuit className="w-6 h-6 mr-2" style={{color: BRAND.cyan}} />
                )}
                <span className="text-2xl font-black text-white tracking-widest">BEQUEST <span style={{color: BRAND.cyan}}>SA.</span></span>
              </div>
              <p className="text-sm mb-6 leading-relaxed">
                Bequest Group is a premier Information and Communication Technology (ICT) consultancy and service provider. We exist to bridge the gap.
              </p>
              <div className="flex space-x-3">
                <div className="inline-block border rounded-lg p-3 text-xs" style={{backgroundColor: BRAND.navyLight, borderColor: `${BRAND.cyan}30`}}>
                  <div className="font-bold text-white mb-1">Company Reg:</div>
                  <div style={{color: BRAND.cyan}}>2025/781851/07</div>
                </div>
                <div className="inline-block border rounded-lg p-3 text-xs" style={{backgroundColor: BRAND.navyLight, borderColor: `${BRAND.cyan}30`}}>
                  <div className="font-bold text-white mb-1">CSD Supplier:</div>
                  <div style={{color: BRAND.cyan}}>MAAA1656690</div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold text-lg mb-6">Services & Partners</h4>
              <ul className="space-y-3 text-sm">
                <li><button onClick={() => setCurrentPage('shop')} className="transition flex items-center hover:text-white"><ChevronRight className="w-3 h-3 mr-2" style={{color: BRAND.cyan}}/> Shop Partner Solutions</button></li>
                <li><a href="#" className="transition flex items-center hover:text-white"><ChevronRight className="w-3 h-3 mr-2" style={{color: BRAND.cyan}}/> Digital Infrastructure</a></li>
                <li><a href="#" className="transition flex items-center hover:text-white"><ChevronRight className="w-3 h-3 mr-2" style={{color: BRAND.cyan}}/> Cybersecurity & Gov</a></li>
                <li><a href="#" className="transition flex items-center hover:text-white"><ChevronRight className="w-3 h-3 mr-2" style={{color: BRAND.cyan}}/> Modern Workplace</a></li>
              </ul>
            </div>

            <div className="col-span-1 md:col-span-2 lg:col-span-2">
              <h4 className="text-white font-bold text-lg mb-6">Head Office Contact</h4>
              <ul className="space-y-4 text-sm p-6 rounded-xl border" style={{backgroundColor: BRAND.navyLight, borderColor: `${BRAND.cyan}20`}}>
                <li className="flex items-start">
                  <Mail className="w-5 h-5 mr-4 flex-shrink-0 mt-1" style={{color: BRAND.cyan}} />
                  <div>
                    <div className="text-white font-semibold mb-1">Email Us</div>
                    <a href="mailto:admin@sabequest.co.za" className="transition hover:text-white">admin@sabequest.co.za</a>
                  </div>
                </li>
                <li className="flex items-start">
                  <Phone className="w-5 h-5 mr-4 flex-shrink-0 mt-1" style={{color: BRAND.cyan}} />
                  <div>
                    <div className="text-white font-semibold mb-1">Call Us</div>
                    <span>+27 62 814 9629</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <Network className="w-5 h-5 mr-4 flex-shrink-0 mt-1" style={{color: BRAND.cyan}} />
                  <div>
                    <div className="text-white font-semibold mb-1">Visit Us</div>
                    <span className="leading-relaxed">Cnr Swart & Brand Drive, President Park<br/>Midrand, Gauteng 1685</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8 flex flex-col md:flex-row justify-between items-center text-xs" style={{borderColor: BRAND.navyLight}}>
            <p>&copy; 2026 Bequest SA (Pty) Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Auth Modal (Sign Up / Login) */}
      {authModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => setAuthModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 flex justify-between items-center text-white" style={{backgroundColor: BRAND.navyMain}}>
               <h3 className="text-xl font-bold flex items-center">
                 <User className="w-5 h-5 mr-2" style={{color: BRAND.cyan}} /> 
                 {authMode === 'signup' ? 'Create an Account' : 'Sign In'}
               </h3>
               <button onClick={() => setAuthModalOpen(false)} className="text-slate-400 hover:text-white transition">
                 <X className="w-6 h-6" />
               </button>
            </div>
            
            <form onSubmit={handleAuthSubmit} className="p-8">
               {authMode === 'signup' && (
                 <div className="text-sm p-3 rounded-lg mb-6 flex items-start" style={{backgroundColor: `${BRAND.cyan}20`, color: BRAND.navyMain, border: `1px solid ${BRAND.cyan}`}}>
                   <Lock className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" style={{color: BRAND.cyanDark}} />
                   <span>Creating an account unlocks exclusive enterprise pricing and online purchasing.</span>
                 </div>
               )}

               {authError && (
                 <div className="text-sm p-3 rounded-lg mb-6 flex items-start" style={{backgroundColor: `${BRAND.red}20`, color: BRAND.red, border: `1px solid ${BRAND.red}`}}>
                   <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                   <span>{authError}</span>
                 </div>
               )}

               <div className="space-y-4">
                 <div>
                   <label className="block text-sm font-bold text-slate-700 mb-1">Email Address <span style={{color: BRAND.red}}>*</span></label>
                   <input 
                     type="email" 
                     required 
                     value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:outline-none transition"
                     style={{outlineColor: BRAND.cyan}}
                     placeholder="admin@sabequest.co.za"
                   />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-slate-700 mb-1">Password <span style={{color: BRAND.red}}>*</span></label>
                   <input 
                     type="password" 
                     required 
                     value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:outline-none transition"
                     style={{outlineColor: BRAND.cyan}}
                     placeholder="Min 8 chars, alphanumeric"
                   />
                   {authMode === 'signup' && (
                     <p className="text-xs text-slate-500 mt-1">Must be at least 8 characters containing both letters and numbers.</p>
                   )}
                 </div>
                 
                 {authMode === 'signup' && (
                   <div>
                     <label className="block text-sm font-bold text-slate-700 mb-1">Confirm Password <span style={{color: BRAND.red}}>*</span></label>
                     <input 
                       type="password" 
                       required 
                       value={confirmPassword}
                       onChange={(e) => setConfirmPassword(e.target.value)}
                       className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:outline-none transition"
                       style={{outlineColor: BRAND.cyan}}
                       placeholder="Confirm your password"
                     />
                   </div>
                 )}
               </div>

               <button type="submit" disabled={authLoading} className="w-full font-bold py-3 rounded-xl mt-8 transition shadow-lg disabled:opacity-60" style={{backgroundColor: BRAND.cyan, color: BRAND.navyMain}}>
                 {authLoading ? 'Please wait...' : (authMode === 'signup' ? 'Create Account & View Pricing' : 'Sign In')}
               </button>

               <div className="mt-6 text-center text-sm text-slate-600">
                 {authMode === 'signup' ? (
                   <>Already have an account? <button type="button" onClick={() => { setAuthMode('login'); setAuthError(''); }} className="font-bold hover:underline" style={{color: BRAND.cyanDark}}>Sign in here</button></>
                 ) : (
                   <>Need an account? <button type="button" onClick={() => { setAuthMode('signup'); setAuthError(''); }} className="font-bold hover:underline" style={{color: BRAND.cyanDark}}>Sign up for pricing</button></>
                 )}
               </div>
            </form>
          </div>
        </div>
      )}

      {/* Cart Sidebar Modal */}
      {isCartOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsCartOpen(false)}></div>
          
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center text-white" style={{backgroundColor: BRAND.navyMain}}>
              <h2 className="text-xl font-bold flex items-center">
                <ShoppingCart className="w-6 h-6 mr-3" style={{color: BRAND.cyan}} /> Your Cart
              </h2>
              <button onClick={() => setIsCartOpen(false)} className="text-slate-400 hover:text-white transition">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 bg-slate-50">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <ShoppingCart className="w-16 h-16 mb-4 opacity-20" />
                  <p>Your cart is currently empty.</p>
                  <button 
                    onClick={() => {setIsCartOpen(false); setCurrentPage('shop');}}
                    className="mt-6 font-bold hover:underline"
                    style={{color: BRAND.cyanDark}}
                  >
                    Browse Solutions
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map(item => (
                    <div key={item.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center">
                      <div className="w-12 h-12 bg-slate-50 rounded flex items-center justify-center mr-4 flex-shrink-0">
                         {item.icon}
                      </div>
                      <div className="flex-grow min-w-0 pr-4">
                        <h4 className="font-bold text-slate-800 text-sm truncate">{item.name}</h4>
                        <div className="font-bold text-sm" style={{color: BRAND.cyanDark}}>R {item.price.toLocaleString()}</div>
                      </div>
                      <div className="flex items-center space-x-2 border border-slate-200 rounded-lg p-1">
                        <button onClick={() => updateQty(item.id, -1)} className="p-1 hover:bg-slate-100 rounded text-slate-500"><Minus className="w-3 h-3" /></button>
                        <span className="text-sm font-bold w-4 text-center">{item.qty}</span>
                        <button onClick={() => updateQty(item.id, 1)} className="p-1 hover:bg-slate-100 rounded text-slate-500"><Plus className="w-3 h-3" /></button>
                      </div>
                      <button onClick={() => removeFromCart(item.id)} className="ml-4 p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t border-slate-200 bg-white">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-slate-600 font-semibold">Subtotal</span>
                  <span className="text-2xl font-black text-slate-900">R {cartTotal.toLocaleString()}</span>
                </div>
                <button className="w-full font-bold py-4 rounded-xl shadow-lg transition" style={{backgroundColor: BRAND.cyan, color: BRAND.navyMain}}>
                  Proceed to Checkout
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <div className={`fixed bottom-6 right-6 text-white px-6 py-4 rounded-xl shadow-2xl transition-all duration-300 transform z-[110] flex items-center ${showToast ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0 pointer-events-none'}`} style={{backgroundColor: BRAND.navyMain, border: `1px solid ${BRAND.cyan}`}}>
        <div className="p-1 rounded-full mr-3" style={{backgroundColor: BRAND.cyan}}>
          <ChevronRight className="w-4 h-4" style={{color: BRAND.navyMain}} />
        </div>
        <span className="font-semibold text-white">Item added to cart</span>
      </div>

    </div>
  );
}